/* eslint-disable @typescript-eslint/indent */
import React, { Component } from 'react';
import styled from 'styled-components';
import { themes, utils } from 'kb-shared';
import { ComprehensionQuestion } from '../';
import { Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import Loader from 'react-loading';
import ComprehensionQuestionBox from '../Components/ComprehensionQuestionBox';
import Button from '../../../components/Button/NewButton';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 20px;
  padding-right: 20px;
`;

export const TitleText = styled.span`
  ${themes.fonts.DomaineDisplay};
  font-size: 28px;
  color: ${themes.colors.KBNight};
`;

const SubtitleText = styled.span`
  ${themes.fonts.FoundersGrotesk};
  font-size: 14px;
  color: ${themes.colors.KBNight};
`;

const QuestionsContainer = styled.div`
  padding-top: 28px;
`;

const OptionContainer = styled.div`
  margin: 18px 0px;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

export const AllSetContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 48px;
`;

export const FinishedSubtitle = styled.span`
  ${themes.fonts.FoundersGrotesk}
  font-size: 20px;
  color: ${themes.colors.KBNight};
  text-align: center;
  padding-top: 8px;
  padding-bottom: 28px;
`;

export const Link = styled.a`
  ${themes.fonts.FoundersGrotesk};
  font-size: 20px;
  color: ${themes.colors.KBNight};
  text-align: center;
  font-weight: bold;
`;

interface CreateComprehensionDecisionResponseData {
  createPatientComprehensionResponse: {
    id: string;
  };
}

const CREATE_COMPREHENSION_DECISION = gql`
  mutation createPatientComprehensionResponse(
    $patientConsentId: Int!
    $comprehensionQuestionId: Int!
    $patientResponse: String!
  ) {
    createPatientComprehensionResponse(
      patientConsentId: $patientConsentId
      comprehensionQuestionId: $comprehensionQuestionId
      patientResponse: $patientResponse
    ) {
      patientComprehensionResponse {
        id
      }
    }
  }
`;

type Props = {
  questions: ComprehensionQuestion[];
  patientConsentId: string;
  onCompleted: () => void;
  onBackToDashboard: () => void;
};

type State = {
  currentQuestion: number;
  loading: boolean;
  responses: {
    [id: string]: string | null;
  };
  displayAnswers: boolean;
};

export default class Comprehension extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const responses = this.generateResponseMap(props.questions);
    this.state = {
      currentQuestion: 0,
      loading: false,
      responses: responses,
      displayAnswers: false
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);

    if (this.props.questions.length === 0) this.submit();
  }

  shouldComponentUpdate(nextProps: Props) {
    if (nextProps.questions !== this.props.questions) {
      const responses = this.generateResponseMap(nextProps.questions);
      this.setState({
        responses
      });
    }
    return true;
  }

  generateResponseMap(questions: ComprehensionQuestion[]) {
    return questions.reduce((map, question) => {
      // @ts-ignore
      map[question.id] = null;
      return map;
    }, {});
  }

  submit() {
    // Inform parent component that form is completed.
    //Parent component will mark form as competed via GraphQL
    this.props.onCompleted();
    this.setState({
      displayAnswers: true
    });
  }

  renderLoader() {
    return (
      <LoadingContainer>
        <Loader type="spin" color={themes.colors.KBYellow} height={150} width={150} />
      </LoadingContainer>
    );
  }

  renderQuestions() {
    if (this.state.loading) {
      return this.renderLoader();
    }
    return (
      <QuestionsContainer>
        {this.props.questions.map((question: ComprehensionQuestion) => {
          return (
            <Mutation<
              CreateComprehensionDecisionResponseData,
              {
                comprehensionQuestionId: number;
                patientConsentId: number;
                patientResponse: string;
              }
            >
              mutation={CREATE_COMPREHENSION_DECISION}
              key={question.id}
            >
              {submitDecision => {
                return (
                  <OptionContainer>
                    <ComprehensionQuestionBox
                      question={question}
                      showCorrectAnswer={this.state.displayAnswers}
                      onSelect={(value: string) => {
                        this.setState({
                          responses: {
                            ...this.state.responses,
                            [question.id]: value
                          }
                        });
                        submitDecision({
                          variables: {
                            comprehensionQuestionId: parseInt(question.id),
                            patientConsentId: parseInt(this.props.patientConsentId),
                            patientResponse: value
                          }
                        });
                      }}
                    />
                  </OptionContainer>
                );
              }}
            </Mutation>
          );
        })}
      </QuestionsContainer>
    );
  }

  renderAllSet() {
    if (!this.state.displayAnswers) {
      return null;
    }

    return (
      <AllSetContainer>
        <TitleText>You&apos;re all set!</TitleText>
        <FinishedSubtitle>
          If you have any questions about what you’ve just reviewed, contact your care team at
          <Link href={`mailto:${utils.KBContacts.clinical}?subject=Consent Forms`}>
            {' '}
            {utils.KBContacts.clinical}{' '}
          </Link>
          or <span style={{ fontWeight: 'bold' }}>1-855-KND-BODY</span>
        </FinishedSubtitle>
        <Button text={'Back to dashboard'} onClick={this.props.onBackToDashboard} />
      </AllSetContainer>
    );
  }

  hasAnsweredAllQuestions() {
    let hasAnswered = true;
    Object.keys(this.state.responses).forEach(questionKey => {
      if (this.state.responses[questionKey] === null) {
        hasAnswered = false;
        return;
      }
    });
    return hasAnswered;
  }

  renderSubmit() {
    if (this.state.displayAnswers) {
      return null;
    }
    const enabled = this.hasAnsweredAllQuestions();
    return (
      <AllSetContainer>
        <Button text={'Submit'} onClick={this.submit.bind(this)} disabled={!enabled} />
      </AllSetContainer>
    );
  }

  render() {
    const { questions } = this.props;
    const hasQuestions = questions.length !== 0;

    if (!hasQuestions) {
      if (this.state.displayAnswers) return this.renderAllSet();

      return this.renderLoader();
    }

    const subtitle = `Please answer the following ${questions.length} questions so we may verify that you’ve understood what you are consenting to.`;

    return (
      <Container>
        <TitleText>Verifying your comprehension:</TitleText>
        <SubtitleText>{subtitle}</SubtitleText>
        {this.renderQuestions()}
        {this.renderSubmit()}
        {this.renderAllSet()}
      </Container>
    );
  }
}
